import { logger } from '@adornis/base/logging.js';
import * as crypto from 'node:crypto';
import { GRADING_THRESHOLDS } from '../../../../shared/constants.js';
import { BoundingBox } from '../../../BoundingBox.js';
import { Point } from '../../../Point.js';
import {
  ErrorRegion,
  LowConfRegion,
  addErrorRegionByExam,
  addLowConfRegionByExam,
  isSolutionArtifact,
  markSolutionTasksDone,
  markUnitFinished,
  type ProgressKey,
} from '../../../extraction-progress.js';
import { aggregateOCRFromBoundingBoxes } from '../../../utils/exam-utils.js';
import type { SolutionSource } from '../../../../shared/solution-source.js';
import { TaskType } from '../../task-types/task-main.js';
import { collectSolutionUnitNodeIds, isSolutionUnitRoot } from '../../pipeline/step-utils.js';
import { createLimiter } from '../../../utils/concurrency.js';

/**
 * THE BOUND ON THIS FILE'S FAN-OUT, and it has to be module-scoped to be a bound at all.
 *
 * `enrichNode` recurses through `Promise.all` at three levels — every root, every child of every node, and every
 * region of every node — with nothing awaited between them. So the tree is in flight all at once and the number of
 * concurrent OCR calls is "every region in the document", a function of the exam that no constant describes.
 *
 * Each of those calls crops from the ORIGINAL page image (`cropAndSaveRegion` does not resize) and sends the crop
 * unresized, because `imagesToBase64URLsWithSizeFallback` only falls back to resizing when a single file exceeds
 * the payload limit and one crop never does. So the fan-out costs a full-resolution decode plus an OCR-enhanced
 * PNG per region, on the main thread and in libuv's pool — which is what saturates the cores and, before this,
 * made the runner refuse admission to everything else.
 *
 * SHARED ACROSS JOBS, on purpose: a shared limiter means two concurrent extractions get eight OCR calls between
 * them rather than eight each. The tree structure then no longer decides the peak — this number does.
 */
const ocrLimiter = createLimiter(8);

import { createBoundingBoxWithOCR } from './ocr-utils.js';
import { OcrExtractionType } from './ocrExtractionTypes.js';
const LOW_CONFIDENCE_THRESHOLD = GRADING_THRESHOLDS.MEDIUM_CONFIDENCE;

export interface RecursiveExtractionTask {
  /** Persisted task identity, carried through by examTaskTreeFromDB — the progress unit key. */
  _id?: string;
  taskNumber: string;
  subtasks: RecursiveExtractionTask[];
  taskType?: string;
  needsAnswer?: boolean;
  description?: string;
  descriptionBoundingBoxes?: BoundingBox[];
  masterSolutionContent?: string;
  masterSolutionBoundingBoxes?: BoundingBox[];
  gradingCriteria?: string;
  gradingBoundingBoxes?: BoundingBox[];
  /**
   * Provenance carried through the DB round-trip (`examTaskTreeFromDB`) so a rebuild preserves it. Never set
   * by an agent: extraction output is an upload by definition, and only already-stored content can be
   * 'generated'. Absent means "this content is new", which `build-exam-tasks` reads as an upload.
   */
  masterSolutionSource?: SolutionSource;
  gradingSchemeSource?: SolutionSource;
  maxPoints?: number;
  /** An answerable parent's OWN answer points (Model C; total = own + children). */
  ownMaxPoints?: number;
  additionalInfoIds?: string[];
  /** Figures placed directly in this task's own body, no `#material` wrapper — see `ExamTaskNode`'s own
   * field of the same name in exam-structure.ts. */
  inlineFigureIds?: string[];
  descriptionWarningCodes?: string[];
}

interface ParsedTaskRegion {
  taskNumber?: string;
  subtasks?: ParsedTaskRegion[];
  maxPoints?: number;
  dependsOn?: string[];
  boundingBoxes?: Array<{
    bbox?: { x1: number; y1: number; x2: number; y2: number };
    pageNumber?: number;
    confidence?: number;
  }>;
}

function buildTaskKey(path: string[]): string {
  return path.join('|');
}

const TASK_NUMBER_PREFIX_PATTERN = /^\s*(?:aufgabe|teilaufgabe|task|exercise|frage|question)\s*[:#.-]?\s*/i;

function normalizeTaskNumberLabel(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';

  let normalized = raw;
  let previous = '';
  while (normalized && normalized !== previous) {
    previous = normalized;
    normalized = normalized.replace(TASK_NUMBER_PREFIX_PATTERN, '').trim();
  }

  normalized = normalized.replace(/^[-:.\s]+/, '').trim();
  return normalized || raw;
}

function normalizeTaskNumbersInTree(value: any): any {
  if (Array.isArray(value)) {
    return value.map(item => normalizeTaskNumbersInTree(item));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const normalizedValue: Record<string, any> = {};
  for (const [key, nestedValue] of Object.entries(value)) {
    if (key === 'taskNumber') {
      normalizedValue[key] = normalizeTaskNumberLabel(nestedValue);
      continue;
    }

    normalizedValue[key] = normalizeTaskNumbersInTree(nestedValue);
  }

  return normalizedValue;
}

export function parseAgentAnswer(result: any): any {
  const rawAnswer = typeof result === 'string' ? result : result?.answer;
  if (!rawAnswer) {
    throw new Error('Agent result does not contain an answer payload');
  }

  const parsedAnswer = typeof rawAnswer === 'string' ? JSON.parse(rawAnswer) : rawAnswer;
  return normalizeTaskNumbersInTree(parsedAnswer);
}

/**
 * Task paths (e.g. "3.a") where the grading-scheme document states DIFFERENT points than the
 * teacher-confirmed structure — announced (never applied silently) so the teacher reviews the
 * discrepancy in the canvas. Compared per task type, matching how the override interprets the
 * document's number:
 *  - LEAF → the document's number is its points; compare vs the confirmed total (`.maxPoints`).
 *  - ANSWERABLE PARENT (has children, `taskType !== TASK_FRAME`) → the document's number is its OWN
 *    answer; compare vs the confirmed own (`.ownMaxPoints`).
 *  - TASK_FRAME → skipped (its number is a derived total, ambiguous/noisy).
 * Only where BOTH sides give a positive number and they differ.
 *
 * @param confirmedTasks the confirmed structure tree (`examTaskTreeFromDB`: `.maxPoints` = total, `.ownMaxPoints` = own)
 * @param parsedGs       the parsed grading-scheme BBX answer (`.tasks[].maxPoints` = the document's points)
 */
export function collectExtractedPointDiffs(confirmedTasks: RecursiveExtractionTask[], parsedGs: any): string[] {
  const positive = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

  const extractedByPath = new Map<string, number>();
  const indexExtracted = (nodes: any[], parentPath: string[]): void => {
    for (const node of Array.isArray(nodes) ? nodes : []) {
      const taskNumber = String(node?.taskNumber ?? '').trim();
      if (!taskNumber) continue;
      const path = [...parentPath, taskNumber];
      const points = positive(node?.maxPoints);
      if (points != null) extractedByPath.set(path.join('.'), points);
      indexExtracted(node?.subtasks, path);
    }
  };
  indexExtracted(parsedGs?.tasks, []);

  const out: string[] = [];
  const walk = (nodes: RecursiveExtractionTask[] | undefined, parentPath: string[]): void => {
    for (const node of Array.isArray(nodes) ? nodes : []) {
      const raw = node as any;
      const taskNumber = String(raw?.taskNumber ?? '').trim();
      if (!taskNumber) continue;
      const path = [...parentPath, taskNumber];
      const children = Array.isArray(raw?.subtasks) ? raw.subtasks : [];
      const extracted = extractedByPath.get(path.join('.'));
      if (extracted != null) {
        const isTaskFrame =
          String(raw?.taskType ?? '')
            .trim()
            .toUpperCase() === TaskType.TASK_FRAME;
        // leaf → confirmed total; answerable parent → confirmed own; task frame → skip.
        const confirmed =
          children.length === 0 ? positive(raw?.maxPoints) : isTaskFrame ? undefined : positive(raw?.ownMaxPoints);
        if (confirmed != null && Math.abs(confirmed - extracted) > 0.001) out.push(path.join('.'));
      }
      walk(children, path);
    }
  };
  walk(confirmedTasks, []);
  return out;
}

export async function enrichTasksWithOCRByBbx(
  tasks: RecursiveExtractionTask[],
  parsedBbx: any,
  imageIds: string[],
  field: 'description' | 'masterSolutionContent' | 'gradingCriteria',
  includeMaxPoints = false,
  onCost?: (cost: number) => void,
  examID?: string,
  step?: ProgressKey,
  progressOwnerId = 'EXAM',
): Promise<RecursiveExtractionTask[]> {
  const parsedTasks = Array.isArray(parsedBbx?.tasks) ? (parsedBbx.tasks as ParsedTaskRegion[]) : [];
  const taskMap = new Map<string, ParsedTaskRegion>();

  const indexParsedTasks = (nodes: ParsedTaskRegion[], parentPath: string[]) => {
    for (const node of nodes) {
      const taskNumber = String(node?.taskNumber ?? '').trim();
      if (!taskNumber) continue;

      const path = [...parentPath, taskNumber];
      taskMap.set(buildTaskKey(path), node);

      if (Array.isArray(node.subtasks) && node.subtasks.length > 0) {
        indexParsedTasks(node.subtasks, path);
      }
    }
  };

  indexParsedTasks(parsedTasks, []);

  const enrichNode = async (task: RecursiveExtractionTask, parentPath: string[]): Promise<RecursiveExtractionTask> => {
    const taskNumber = String(task?.taskNumber ?? '').trim();
    const path = [...parentPath, taskNumber];
    const key = buildTaskKey(path);
    const parsedTask = taskMap.get(key);
    const regions = Array.isArray(parsedTask?.boundingBoxes) ? parsedTask.boundingBoxes : [];
    const additionalInfoIds = Array.isArray(parsedTask?.dependsOn) ? parsedTask.dependsOn : [];
    const failedBoundingBoxIds = new Set<string>();

    const enrichedTask: RecursiveExtractionTask = {
      ...task,
      subtasks: [],
    };

    if (regions.length > 0) {
      try {
        const validRegions = regions.filter(region => region?.bbox && typeof region.pageNumber === 'number');
        const boundingBoxes = await Promise.all(
          validRegions.map(async region => {
            const boundingBoxId = crypto.randomUUID();
            const pageIdx = Number(region.pageNumber) - 1;
            const sourceImageId = pageIdx >= 0 && pageIdx < imageIds.length ? imageIds[pageIdx] : null;
            try {
              const currentTaskType = String(task?.taskType ?? '').trim();
              const bbox = await ocrLimiter(() =>
                createBoundingBoxWithOCR(
                  {
                    bbox: region.bbox as { x1: number; y1: number; x2: number; y2: number },
                    pageNumber: region.pageNumber!,
                    confidence: typeof region.confidence === 'number' ? region.confidence : undefined,
                    boundingBoxId,
                  },
                  imageIds,
                  field === 'description'
                    ? OcrExtractionType.TASK_DESCRIPTION
                    : field === 'masterSolutionContent'
                      ? OcrExtractionType.MASTER_SOLUTION
                      : OcrExtractionType.GRADING_CRITERIA,
                  currentTaskType || TaskType.DEFAULT,
                  onCost,
                ),
              );
              return bbox;
            } catch (error) {
              logger.warn(
                {
                  err: error,
                  field,
                  taskPath: path.join('/'),
                  pageNumber: region.pageNumber,
                  imageId: sourceImageId ?? 'n/a',
                  context: 'task-enrichment',
                },
                'OCR enrichment failed for field at task path',
              );
              if (examID && step) {
                await addErrorRegionByExam(
                  examID,
                  step,
                  new ErrorRegion({
                    submissionId: progressOwnerId,
                    taskPath: path,
                    additionalInfo: false,
                    boundingBoxId,
                  }),
                );
              }
              if (field !== 'description') {
                failedBoundingBoxIds.add(boundingBoxId);
              }
              return new BoundingBox({
                _id: boundingBoxId,
                topLeft: new Point({
                  x: (region.bbox as { x1: number; y1: number; x2: number; y2: number }).x1,
                  y: (region.bbox as { x1: number; y1: number; x2: number; y2: number }).y1,
                }),
                bottomRight: new Point({
                  x: (region.bbox as { x1: number; y1: number; x2: number; y2: number }).x2,
                  y: (region.bbox as { x1: number; y1: number; x2: number; y2: number }).y2,
                }),
                pageNumber: region.pageNumber!,
                imageId: sourceImageId,
                ocrExtractedText: '',
                confidence: 0,
                isHandwritten: false,
              });
            }
          }),
        );

        if (examID && step) {
          const lowConfidenceBoundingBoxes = boundingBoxes.filter(
            bbox =>
              typeof bbox.confidence === 'number' &&
              bbox.confidence < LOW_CONFIDENCE_THRESHOLD &&
              !failedBoundingBoxIds.has(String(bbox._id ?? '')),
          );

          for (const lowConfidenceBoundingBox of lowConfidenceBoundingBoxes) {
            await addLowConfRegionByExam(
              examID,
              step,
              new LowConfRegion({
                submissionId: progressOwnerId,
                taskPath: path,
                additionalInfo: false,
                boundingBoxId: String(lowConfidenceBoundingBox._id ?? ''),
              }),
            );
          }
        }

        const aggregated = aggregateOCRFromBoundingBoxes(boundingBoxes);
        (enrichedTask as any)[field] = aggregated;

        const bbxFieldMap = {
          description: 'descriptionBoundingBoxes',
          masterSolutionContent: 'masterSolutionBoundingBoxes',
          gradingCriteria: 'gradingBoundingBoxes',
        } as const;
        (enrichedTask as any)[bbxFieldMap[field]] = boundingBoxes;
      } catch (error) {
        logger.error(
          { err: error, field, taskPath: path.join('/'), context: 'task-enrichment' },
          'Failed OCR enrichment for field at task path',
        );
      }
    }

    if (additionalInfoIds.length > 0) {
      enrichedTask.additionalInfoIds = additionalInfoIds;
    }

    if (includeMaxPoints) {
      // The uploaded grading scheme's points ALWAYS win (first upload or reupload). The confirmed structure
      // carries the Model-C point fields on the task — declaredMaxPoints (total) and ownMaxPoints (a parent's
      // own answer) — and build-exam-tasks PREFERS those over the recursive `maxPoints`. So when the document
      // states points we must overwrite those fields too, or the structure points stick. Tasks the document
      // doesn't mention keep their confirmed values (spread above). Diffs are announced separately.
      const docPoints = typeof parsedTask?.maxPoints === 'number' ? parsedTask.maxPoints : undefined;
      if (docPoints != null) {
        const hasKids = Array.isArray(task.subtasks) && task.subtasks.length > 0;
        const isAnswerableParent =
          hasKids &&
          String(task.taskType ?? '')
            .trim()
            .toUpperCase() !== TaskType.TASK_FRAME;
        if (isAnswerableParent) {
          // Answerable parent: the document's points are its OWN answer; its total stays dynamic (own + children).
          enrichedTask.ownMaxPoints = docPoints;
          enrichedTask.maxPoints = undefined;
        } else {
          // Leaf or task frame: the document's points are the task's total.
          enrichedTask.maxPoints = docPoints;
          enrichedTask.ownMaxPoints = undefined;
        }
      }
      // else: the document detected no points here → keep the confirmed values (spread above).
    }

    // ANNOUNCED FROM INSIDE THIS PATH, before recursing, and that placement is the whole point. Every node is
    // one branch of a `Promise.all`, so the branches finish independently and at different times — this node's
    // own regions are through OCR right here. Marking it after the children below would tie its announcement to
    // the slowest leaf under it, and a root's announcement to the slowest leaf in the exam: with four roots
    // awaited together, all four marks would then land at once and the bar would show a single jump from 0 to 4
    // no matter how many units it had. Announcing per branch as the branch lands is what makes it move.
    //
    // A COUNTER STEP'S UNIT IS THE NODE — every task at any depth, which is what `countRecursiveTasks` sets as
    // the total. Nothing forces that unit to be coarser here: the structure step is the only producer of its
    // progress record, so it is free to report at the granularity its work actually has. A solution artifact is
    // not, and the second mark below is where that constraint shows up.
    if (examID && step && !isSolutionArtifact(step)) {
      await markUnitFinished(examID, step, key);
    }

    const childTasks = Array.isArray(task.subtasks) ? task.subtasks : [];
    enrichedTask.subtasks = await Promise.all(childTasks.map(child => enrichNode(child, path)));

    // A SOLUTION ARTIFACT'S UNIT IS EVERY GRADABLE NODE (see SolutionProgress's doc comment), so the mark
    // belongs here — after the subtree — because none of them are genuinely done until every task under
    // this root has its content: one bbx/OCR pass covers the whole root at once, the same way one LLM call
    // does for a generation. `collectSolutionUnitNodeIds` is what maps the root down to its actual units;
    // `_id` is the right id for them because these two steps enrich BUILT task records, which have one.
    //
    // A counter step could not use `_id` even if it wanted the same shape, and that was a live bug: the
    // structure step enriches the structure agent's RAW JSON, and `buildExamTasksFromRecursiveStructure` is
    // what mints ids, running after this. `String(task._id)` was therefore the literal string "undefined" on
    // every node of every structure run, all of them collapsed onto that one entry in the `$addToSet`, and the
    // bar sat at 1 of N from the first node to the last. Hence `key` above — the task-number path the region
    // lookup already addresses nodes by, stable across a re-extraction.
    if (examID && step && isSolutionArtifact(step) && parentPath.length === 0 && isSolutionUnitRoot(task)) {
      await markSolutionTasksDone(examID, step, collectSolutionUnitNodeIds(task));
    }

    return enrichedTask;
  };

  return Promise.all((Array.isArray(tasks) ? tasks : []).map(task => enrichNode(task, [])));
}
